import { useEffect, useState } from 'react';
import { useAPIRoute } from './useAPIRoute';
import { ENGAGEMENT_QUEUE_ID } from './useEnqueueEngagement';
import { useEngagementQueue } from 'rwl-ui';

const PUSH_INTERVAL_IN_SECONDS = 5;

function sToMs(seconds: number) {
    return seconds * 1000;
}

type TimeoutType = ReturnType<typeof setTimeout>;

type UsePushEngagementsProps = {
    sessionId: string | undefined;
    intervalInSeconds?: number;
};

/**
 * Automatically push the engagement events to the server at a regular interval
 */
export const usePublishEngagements = ({
    sessionId,
    intervalInSeconds = PUSH_INTERVAL_IN_SECONDS,
}: UsePushEngagementsProps) => {
    const url = useAPIRoute('engagement/events');
    const { queue, hasQueue } = useEngagementQueue(ENGAGEMENT_QUEUE_ID);

    const [timeoutHandle, setTimeoutHandle] = useState<
        TimeoutType | undefined
    >();

    useEffect(() => {
        // Clear the timeout if the sessionId changes
        if (timeoutHandle) {
            clearTimeout(timeoutHandle);
        }

        // Don't start the timeout if there is no sessionId
        if (!sessionId || !hasQueue || !queue) {
            // the !queue check is redundant but it lets TS know that queue is defined
            return;
        }

        // Create the timeout function
        const runInterval = (backoff = 0) => {
            const nextRunIntervalInSeconds = Math.min(
                600,
                2 ** backoff * intervalInSeconds
            );

            const handle = setTimeout(async () => {
                const events = queue.getQueue();
                if (events.length === 0) {
                    // try again in PUSH_INTERVAL seconds
                    runInterval();
                    return;
                }

                try {
                    await fetch(url, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            sessionId,
                            events,
                        }),
                    }).then(async (result) => {
                        if (result.status !== 200) {
                            throw new Error(
                                `Server responded with ${
                                    result.status
                                } ${await result.text()}`
                            );
                        }
                        // clear the queue if the events were received by the server
                        queue.clearQueue();
                        // try again in PUSH_INTERVAL seconds
                        runInterval();
                    });
                } catch (err: any) {
                    const retryInterval = Math.min(
                        600,
                        nextRunIntervalInSeconds * 2
                    );
                    const error = new Error(
                        `Failed to publish engagement events, trying again in ${retryInterval} seconds - ${err.message}`
                    );
                    console.error(error);
                    // try again with backoff
                    runInterval(backoff + 1);
                }
            }, sToMs(nextRunIntervalInSeconds));

            setTimeoutHandle(handle);
        };

        // Start the timeout function
        runInterval();

        // Clear the timeout when the component unmounts
        return () => {
            clearTimeout(timeoutHandle);
        };
    }, [sessionId, hasQueue]);
};
